import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { H3, PSmall } from '../elements/Text';
import { ROUTES } from '../../routing';

export default function AssessmentTests({
  assessmentTests,
}: {
  assessmentTests: Array<{
    sessionId: number;
    testSlug: string;
    startedAt: Date;
    finishedAt: Date | null;
    companySlug: string;
    testTitle: string;
  }>;
}) {
  const history = useHistory();

  const getStatus = (test: { startedAt: Date; finishedAt: Date | null }) => {
    if (test.finishedAt) {
      return { label: 'Finished', color: 'primary' as const };
    }
    if (test.startedAt) {
      return { label: 'In Progress', color: 'secondary' as const };
    }
    return { label: 'Invited', color: 'textRegular' as const };
  };

  return (
    <>
      <H3>Your Assessments</H3>
      <AssessmentTestsContainer>
        {assessmentTests.map((test, index) => {
          const status = getStatus(test);
          return (
            <AssessmentTestCard
              key={test.sessionId}
              title={test.testTitle}
              status={status}
              onClick={() => {
                history.push(
                  ROUTES.TEAMS.ASSESSMENT_TEST.getPath({
                    teamSlug: test.companySlug,
                    testSlug: test.testSlug,
                  })
                );
              }}
              isFirst={index === 0}
              isLast={index === assessmentTests.length - 1}
            />
          );
        })}
      </AssessmentTestsContainer>
    </>
  );
}

const AssessmentTestsContainer = styled.div`
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-radius: 12px;
  padding: 0;
  margin: 32px 0;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const AssessmentTestCardWrapper = styled.div<{
  isFirst: boolean;
  isLast: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 18px;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.colors.bgActive};
  }

  &:last-child {
    border-bottom: none;
  }

  ${({ isFirst }) =>
    isFirst &&
    css`
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    `}

  ${({ isLast }) =>
    isLast &&
    css`
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    `}
`;

const AssessmentTestIcon = styled.i.attrs({
  className: 'fa-solid fa-list-check',
})`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 32px;
  margin-top: 0;
  padding: 0 20px 0;
`;

const AssessmentTestInfo = styled.div`
  flex: 1;
`;

const AssessmentTestTitle = styled(PSmall)`
  font-weight: 700;
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const AssessmentTestDescription = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin-top: 2px;
`;

const AssessmentTestStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AssessmentTestButton = styled(PSmall)<{ isCompleted: boolean }>`
  color: ${({ theme, isCompleted }) =>
    !isCompleted ? theme.colors.primary : theme.colors.textRegular};
  font-weight: 600;
  margin-top: 0;
  padding-bottom: 0;
`;

const AssessmentTestArrowIcon = styled.i.attrs({
  className: 'fa-solid fa-forward',
})<{ isCompleted: boolean }>`
  color: ${({ theme, isCompleted }) =>
    !isCompleted ? theme.colors.primary : theme.colors.textRegular};
  font-size: 16px;
  margin-top: 0;
  margin-right: 10px;
`;

function AssessmentTestCard({
  title,
  status,
  onClick,
  isFirst,
  isLast,
}: {
  title: string;
  status: { label: string; color: 'primary' | 'secondary' | 'textRegular' };
  onClick: () => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  const getTestDescription = (status: string) => {
    if (status === 'Finished') {
      return 'You have completed this assessment.';
    }
    if (status === 'In Progress') {
      return 'Continue where you left off in this assessment.';
    }
    return 'Complete this assessment to understand your skill profile.';
  };

  const getButtonText = (statusLabel: string) => {
    switch (statusLabel) {
      case 'Finished':
        return 'Open';
      case 'In Progress':
        return 'Continue';
      default:
        return 'Start';
    }
  };

  const isCompleted = status.label === 'Finished';

  return (
    <AssessmentTestCardWrapper
      onClick={onClick}
      isFirst={isFirst}
      isLast={isLast}
    >
      <AssessmentTestIcon />
      <AssessmentTestInfo>
        <AssessmentTestTitle>{title}</AssessmentTestTitle>
        <AssessmentTestDescription>
          {getTestDescription(status.label)}
        </AssessmentTestDescription>
      </AssessmentTestInfo>
      <AssessmentTestStatus>
        <AssessmentTestButton isCompleted={isCompleted}>
          {getButtonText(status.label)}
        </AssessmentTestButton>
        <AssessmentTestArrowIcon isCompleted={isCompleted} />
      </AssessmentTestStatus>
    </AssessmentTestCardWrapper>
  );
}
